// ─── Il client dell'API, e dov'è custodito il gettone ────────────────────────
//
// 🔒 IL GETTONE DI ACCESSO STA IN MEMORIA, e in nessun altro posto. Non in
// `localStorage`, non in un cookie leggibile: uno script che riuscisse a finire
// nella pagina se lo porterebbe via, e se lo porterebbe via anche dopo, perché
// `localStorage` sopravvive alla chiusura della scheda.
//
// ⚖️ Il prezzo c'è ed è dichiarato: ricaricando la pagina il gettone sparisce.
// Non è un problema, è il disegno — a ricostruirlo è il cookie `HttpOnly` del
// rinnovo, che il JavaScript non vede e che il browser manda da sé alle sole
// rotte di `/auth`.

import {
  profiloSchema,
  sessioneApertaSchema,
  type CapacitaAccesso,
  type Profilo,
  type SessioneAperta,
} from '@pmo/shared';

const BASE = '/api/v1';

/** L'errore come lo manda l'API: un codice per il codice, un messaggio per chi legge. */
export class ErroreApi extends Error {
  constructor(
    readonly stato: number,
    readonly codice: string,
    messaggio: string,
    readonly dettagli?: unknown,
  ) {
    super(messaggio);
    this.name = 'ErroreApi';
  }
}

let gettone: string | null = null;
let profilo: Profilo | null = null;
let ripresaFatta = false;

type Ascoltatore = () => void;
const ascoltatori = new Set<Ascoltatore>();

export function osservaSessione(a: Ascoltatore): () => void {
  ascoltatori.add(a);
  return () => ascoltatori.delete(a);
}
export function profiloCorrente(): Profilo | null {
  return profilo;
}
/**
 * Il gettone, per l'unico caso in cui non può viaggiare in un'intestazione: il
 * WebSocket del tempo reale.
 *
 * ⚠️ L'oggetto `WebSocket` dei browser non permette di aggiungere
 * `Authorization`, quindi il gettone va nell'indirizzo. È l'unica strada, ed è
 * il motivo per cui esiste questa funzione: **non è un invito a portarselo in
 * giro**. Ogni altra chiamata passa da `chiama()`, che lo mette dove va.
 */
export function gettoneCorrente(): string | null {
  return gettone;
}
/** `false` finché non si sa se c'è una sessione da riprendere dal cookie. */
export function sessioneRipresa(): boolean {
  return ripresaFatta;
}
function annuncia(nuovo: Profilo | null, t: string | null): void {
  profilo = nuovo;
  gettone = t;
  for (const a of ascoltatori) a();
}

async function grezza(metodo: string, rotta: string, corpo?: unknown): Promise<Response> {
  return fetch(BASE + rotta, {
    method: metodo,
    headers: {
      ...(corpo === undefined ? {} : { 'content-type': 'application/json' }),
      ...(gettone ? { authorization: `Bearer ${gettone}` } : {}),
    },
    body: corpo === undefined ? undefined : JSON.stringify(corpo),
    // Il cookie del rinnovo viaggia da sé, ma solo se glielo si lascia fare.
    credentials: 'same-origin',
  });
}

async function leggi(res: Response): Promise<unknown> {
  if (res.status === 204) return null;
  const testo = await res.text();
  if (testo === '') return null;
  try {
    return JSON.parse(testo);
  } catch {
    throw new ErroreApi(
      res.status,
      'RISPOSTA_ILLEGGIBILE',
      'Il server ha risposto qualcosa che non so leggere.',
    );
  }
}

function comeErrore(res: Response, corpo: unknown): ErroreApi {
  const e = (corpo as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
  return new ErroreApi(
    res.status,
    e?.code ?? 'ERRORE',
    // Il messaggio dell'API è già in italiano e già per la segreteria: si mostra
    // quello. Un codice nudo davanti a chi lavora non è un errore, è un enigma.
    e?.message ?? 'Qualcosa non ha funzionato.',
    e?.details,
  );
}

/**
 * Un solo rinnovo per volta.
 *
 * ⚠️ Senza questa promessa condivisa, cinque richieste che scadono insieme —
 * cosa normale, perché scadono tutte allo stesso istante — farebbero cinque
 * rinnovi in parallelo. E siccome il rinnovo RUOTA il gettone, le ultime
 * quattro rigiocherebbero un cookie già consumato: il server lo legge come un
 * furto e chiude tutte le sessioni. Cioè: l'applicativo butterebbe fuori sé
 * stesso, e lo farebbe proprio quando è più occupato.
 */
let rinnovoInCorso: Promise<boolean> | null = null;

function rinnova(): Promise<boolean> {
  rinnovoInCorso ??= (async () => {
    try {
      const res = await grezza('POST', '/auth/refresh');
      if (!res.ok) return false;
      const s = sessioneApertaSchema.parse(await leggi(res));
      annuncia(s.user, s.accessToken);
      return true;
    } catch {
      return false;
    } finally {
      // Si azzera al giro dopo, non subito: così le richieste che stanno
      // aspettando questa promessa la vedono risolversi.
      setTimeout(() => {
        rinnovoInCorso = null;
      }, 0);
    }
  })();
  return rinnovoInCorso;
}

export async function chiama<T>(metodo: string, rotta: string, corpo?: unknown): Promise<T> {
  let res = await grezza(metodo, rotta, corpo);

  // Un 401 su una rotta qualunque vuol dire «il gettone è scaduto»: si prova a
  // rinnovarlo UNA volta e si ripete. Se il rinnovo non riesce, la sessione è
  // finita davvero, e si torna alla schermata di accesso senza far finta.
  if (res.status === 401 && !rotta.startsWith('/auth/refresh')) {
    if (await rinnova()) {
      res = await grezza(metodo, rotta, corpo);
    } else {
      annuncia(null, null);
    }
  }

  const corpoRisposta = await leggi(res);
  if (!res.ok) throw comeErrore(res, corpoRisposta);
  return corpoRisposta as T;
}

// ── I gesti dell'accesso ────────────────────────────────────────────────────

async function apri(rotta: string, corpo: unknown): Promise<SessioneAperta> {
  const res = await grezza('POST', rotta, corpo);
  const risposta = await leggi(res);
  if (!res.ok) throw comeErrore(res, risposta);
  const s = sessioneApertaSchema.parse(risposta);
  annuncia(s.user, s.accessToken);
  return s;
}

export const entra = (email: string, password: string) => apri('/auth/login', { email, password });

export const creaAccesso = (email: string, password: string) => apri('/auth/register', { email, password });

export async function esci(): Promise<void> {
  try {
    await chiama('POST', '/auth/logout');
  } finally {
    // Anche se la chiamata fallisce, da questa parte si esce comunque: tenere
    // la pagina «dentro» quando l'utente ha detto di uscire è la cosa peggiore.
    annuncia(null, null);
  }
}

/**
 * Al caricamento della pagina: c'è una sessione da riprendere dal cookie?
 *
 * 🚨 ANNUNCIA SEMPRE, anche — e soprattutto — quando NON c'è niente da
 * riprendere. `rinnova()` avvisa gli ascoltatori solo quando riesce, e questo
 * bastava finché si guardava il caso «c'era una sessione»: nel caso opposto
 * nessuno avvisava nessuno, e la pagina restava su «Un attimo…» **per chiunque
 * non fosse già dentro** — cioè per tutti quelli che devono fare l'accesso.
 * 📏 Trovato aprendo la pagina con un browser vero, non rileggendo: a schermo
 *    il difetto era intermittente, perché a volte React ridisegnava per conto
 *    suo e la schermata di accesso compariva lo stesso.
 * 📌 Un guasto che si cura da solo una volta su due non è meno grave: è più
 *    difficile da credere.
 */
export async function riprendi(): Promise<Profilo | null> {
  try {
    await rinnova();
  } finally {
    ripresaFatta = true;
    annuncia(profilo, gettone);
  }
  return profilo;
}

/** Rilegge il profilo dal server: è così che si vede un permesso cambiato. */
export async function rileggiProfilo(): Promise<Profilo | null> {
  try {
    const p = profiloSchema.parse(await chiama('GET', '/auth/me'));
    annuncia(p, gettone);
    return p;
  } catch {
    return profilo;
  }
}

export async function cambiaPassword(attuale: string, nuova: string): Promise<void> {
  await chiama('POST', '/auth/password/change', {
    currentPassword: attuale,
    newPassword: nuova,
  });
  await rileggiProfilo();
}

// ── La riapertura dell'accesso ──────────────────────────────────────────────

/**
 * Cosa sa fare questo gestionale prima che si sia entrati.
 *
 * ⭐ Serve a non offrire «ho dimenticato la password» quando quel bottone non
 * potrebbe mandare niente a nessuno: senza questa domanda, chi lo preme
 * resterebbe ad aspettare un messaggio che non parte, e non avrebbe modo di
 * saperlo. La risposta non dice niente su nessun account — è un fatto della
 * configurazione del server.
 */
export const capacitaAccesso = () => chiama<CapacitaAccesso>('GET', '/auth/capabilities');

/** Risponde `ok` in ogni caso, apposta: da fuori non si deve capire quali email esistono. */
export const chiediRiapertura = (email: string) =>
  chiama<{ ok: true }>('POST', '/auth/password/forgot', { email });

export const riapriConGettone = (token: string, newPassword: string) =>
  chiama<{ ok: true }>('POST', '/auth/password/reset', { token, newPassword });

/**
 * Scarica un file dall'API.
 *
 * 🚨 NON si può usare un `<a href>` nudo, e il motivo è il disegno di questo
 * client: il gettone di accesso **sta in memoria**, non in un cookie — quindi
 * un download avviato dal browser non lo porterebbe con sé e tornerebbe un
 * 401. ⇒ Il file si chiede come qualunque altra cosa, con l'intestazione al
 * posto giusto, e poi si consegna al browser come un oggetto in memoria.
 *
 * ⛔ L'indirizzo temporaneo si revoca subito dopo: senza, ogni export lascia
 * in vita una copia del file finché la scheda non si chiude — cioè per tutta
 * la giornata della segreteria.
 */
export async function scarica(rotta: string, nomeProposto: string): Promise<void> {
  let res = await grezza('GET', rotta);
  if (res.status === 401) {
    if (await rinnova()) res = await grezza('GET', rotta);
    else annuncia(null, null);
  }
  if (!res.ok) throw comeErrore(res, await leggi(res));

  const contenuto = await res.blob();
  // Il nome vero lo dice il server; questo è il ripiego quando l'intestazione
  // non arriva (succede dietro certi proxy).
  const dichiarato = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1];
  const indirizzo = URL.createObjectURL(contenuto);
  const a = document.createElement('a');
  a.href = indirizzo;
  a.download = dichiarato ?? nomeProposto;
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(indirizzo);
}

/**
 * 📄 Apre un file in una scheda nuova (23/10/2026: il certificato medico). Come `scarica`, il gettone non viaggia
 * con un `<a href>` nudo: il file si chiede con l'intestazione e si apre come oggetto in memoria.
 * ⚠️ La scheda si apre SUBITO, al tocco: aperta dopo l'attesa, il browser la tratterebbe come una finestra non
 * chiesta e la bloccherebbe.
 */
export async function apriFile(rotta: string): Promise<void> {
  const scheda = window.open('', '_blank');
  try {
    let res = await grezza('GET', rotta);
    if (res.status === 401) {
      if (await rinnova()) res = await grezza('GET', rotta);
      else annuncia(null, null);
    }
    if (!res.ok) throw comeErrore(res, await leggi(res));
    const indirizzo = URL.createObjectURL(await res.blob());
    if (scheda) scheda.location.href = indirizzo;
    else window.location.assign(indirizzo);
    // Il tempo di caricarlo nella scheda, poi la copia in memoria se ne va.
    setTimeout(() => URL.revokeObjectURL(indirizzo), 60_000);
  } catch (e) {
    scheda?.close();
    throw e;
  }
}
